import React, { useState } from 'react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import LOCATIONS from '../../constants/locations'
import DistributionChart from '../shared/DistributionChart'
import { QUESTS } from '../../constants/quests'
import { LocationEnum, SkillEnum } from '../../types'
import { TravelModal } from '../modals/TravelModal'
import { SURF_SPOTS } from '@/constants/surfSpots'
import { SkillPill } from '../shared/SkillPill'
import { MapPin, HelpCircle } from 'lucide-react'
import { getTravelCostsFromCurrentLocation } from '@/constants/travelCosts'
import { Tooltip } from '@nextui-org/react'
import { isDemo } from '@/utils'

export const LocationsScreen: React.FC = () => {
  const { gameData } = useGameplayContext()
  const [travelModalData, setTravelModalData] = useState<{
    toLocation: LocationEnum
    toSpot: string
  } | null>(null)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isLocationUnlocked = (locationKey: LocationEnum): boolean => {
    if (locationKey === 'mediterranean') return true // First location is always unlocked
    if (isDemo()) {
      return locationKey === 'france' && gameData.completedChapters.includes('mediterranean')
    } else {
      return (
        gameData.completedChapters.includes(locationKey) ||
        gameData.completedQuests.some((questKey) => QUESTS[questKey].effect.locationUnlocked === locationKey)
      )
    }
  }

  const isSurfSpotUnlocked = (locationKey: LocationEnum, spot: string): boolean => {
    if (LOCATIONS[locationKey].surfSpots.indexOf(spot) === 0) return true // First surf spot is always unlocked
    return gameData.completedQuests.some(
      (questKey) => QUESTS[questKey].effect.surfSpotUnlocked === spot && QUESTS[questKey].location === locationKey
    )
  }

  const unlockedLocations = Object.values(LOCATIONS).filter((location) => isLocationUnlocked(location.key))

  const handleTravelClick = (toLocation: LocationEnum, toSpot: string) => {
    // Don't open modal if clicking on the current spot
    if (gameData.currentLocation === toLocation && gameData.currentLocationSpot === toSpot) {
      return
    }
    setTravelModalData({ toLocation, toSpot })
  }

  const handleTravelConfirm = () => {
    setTravelModalData(null)
  }

  const handleTravelCancel = () => {
    setTravelModalData(null)
  }

  const tooltipContent = (
    <div className="max-w-md space-y-1 p-2">
      <p className="pt-2 pb-2 border-b border-gray-300">
        🌍 Each location has unique characteristics shown in the charts: wave heights, types, maximum ride times, and
        crowd levels.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        🌊 Later locations offer better conditions but also more challenging waves - you'll need to improve your skills
        to master them.
      </p>
      <p className="pt-2 pb-2">
        💰 Each surf spot provides specific skill bonuses. Travel costs money, especially between locations, so plan
        your trips carefully!
      </p>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h2 className="text-3xl font-bold text-white drop-shadow-md">🌍 Locations</h2>
        <Tooltip
          content={tooltipContent}
          isOpen={isTooltipOpen}
          onOpenChange={(open) => setIsTooltipOpen(open)}
          className="bg-white backdrop-blur-sm text-sm"
          placement="bottom"
        >
          <button
            className="text-white hover:text-blue-300 transition-colors"
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <HelpCircle className="w-6 h-6" />
          </button>
        </Tooltip>
      </div>
      <div className="space-y-6">
        {unlockedLocations.map((location) => (
          <div key={location.key} className="bg-blue-600 bg-opacity-50 rounded-lg p-4 shadow-lg text-white">
            <h3 className="text-2xl font-semibold mb-4">{location.name}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <DistributionChart data={location.waveHeight} title="Wave Height" />
              <DistributionChart data={location.waveType} title="Wave Type" />
              <DistributionChart data={location.waveTime} title="Max Ride Time (seconds)" />
              <DistributionChart data={location.crowd} title="Crowd" />
            </div>

            <div className="mt-6">
              <h4 className="text-xl font-semibold mb-3">Travel To</h4>
              <div className="flex flex-col gap-3">
                {location.surfSpots
                  .filter((spot) => isSurfSpotUnlocked(location.key, spot))
                  .map((spot) => (
                    <div key={spot} className="flex flex-row gap-2">
                      <button
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 w-fit ${
                          gameData.currentLocation === location.key && gameData.currentLocationSpot === spot
                            ? 'bg-white text-green-600'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                        onClick={() => handleTravelClick(location.key, spot)}
                        title={spot}
                      >
                        <MapPin className="w-4 h-4" />
                        {spot}{' '}
                        {gameData.currentLocationSpot === spot
                          ? '(current)'
                          : `($${getTravelCostsFromCurrentLocation(gameData, location.key)})`}
                      </button>
                      <div className="flex flex-wrap gap-1 ml-1">
                        {SURF_SPOTS[spot]?.map((multiplier) => (
                          <SkillPill
                            key={multiplier.key}
                            skill={multiplier.key as SkillEnum}
                            value={multiplier.value}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {travelModalData && (
        <TravelModal
          toLocation={travelModalData.toLocation}
          toSpot={travelModalData.toSpot}
          onConfirm={handleTravelConfirm}
          onCancel={handleTravelCancel}
        />
      )}
    </div>
  )
}
